import {
  useCallback,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

import {
  clampTransportPosition,
  type TransportPosition,
} from "@/lib/timeline/view/TimelineUiPreferences/prefs";

interface UseTransportDragOptions {
  stageRef: RefObject<HTMLElement | null>;
  transportRef: RefObject<HTMLElement | null>;
  position: TransportPosition;
  onPositionChange: (position: TransportPosition) => void;
}

export function useTransportDrag({
  stageRef,
  transportRef,
  position,
  onPositionChange,
}: UseTransportDragOptions) {
  const positionRef = useRef(position);
  positionRef.current = position;

  const onHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      const stage = stageRef.current;
      const transport = transportRef.current;
      if (stage === null || transport === null) {
        return;
      }

      const transportRect = transport.getBoundingClientRect();
      const centerX = transportRect.left + transportRect.width / 2;
      const centerY = transportRect.top + transportRect.height / 2;
      const dragOffsetX = event.clientX - centerX;
      const dragOffsetY = event.clientY - centerY;

      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);

      const updateFromClient = (clientX: number, clientY: number): void => {
        const rect = stage.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1) {
          return;
        }
        const nextCenterX = clientX - dragOffsetX;
        const nextCenterY = clientY - dragOffsetY;
        const next = clampTransportPosition({
          x: (nextCenterX - rect.left) / rect.width,
          y: (nextCenterY - rect.top) / rect.height,
        });
        positionRef.current = next;
        onPositionChange(next);
      };

      const onPointerMove = (moveEvent: PointerEvent): void => {
        updateFromClient(moveEvent.clientX, moveEvent.clientY);
      };

      const onPointerUp = (upEvent: PointerEvent): void => {
        target.releasePointerCapture(upEvent.pointerId);
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", onPointerUp);
        target.removeEventListener("pointercancel", onPointerUp);
        onPositionChange(positionRef.current);
      };

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", onPointerUp);
      target.addEventListener("pointercancel", onPointerUp);
    },
    [onPositionChange, stageRef, transportRef],
  );

  return { onHandlePointerDown };
}
